import { useMemo } from "react";
import { useTokenEligibility } from "./useTokenEligibility.hook";
import type { GeneralTokenCollectionType } from "@/features/bridge/types/open-sea-api.types";

export interface CollectionEligibilityResult {
  hasEligibleTokens: boolean;
  eligibleTokenCount: number;
  totalTokenCount: number;
  isLoading: boolean;
}

/**
 * Hook to check if a collection has any bridgeable tokens
 * Only checks one token per collection since all tokens in a collection share the same contract
 * @param collection - The collection to check
 * @returns Collection eligibility information
 */
export const useCollectionEligibility = (
  collection: GeneralTokenCollectionType[string] | undefined
): CollectionEligibilityResult => {
  // Check eligibility using the collection's contract address
  // All tokens in the collection will have the same eligibility since they share the same contract
  const contractAddress = collection?.collectionData?.address;
  const eligibility = useTokenEligibility(contractAddress);

  return useMemo(() => {
    if (!collection?.tokenData) {
      return {
        hasEligibleTokens: false,
        eligibleTokenCount: 0,
        totalTokenCount: 0,
        isLoading: false,
      };
    }

    // Since all tokens in a collection share the same contract address,
    // they all have the same eligibility status
    const hasEligibleTokens = eligibility.isEligible;
    const totalTokenCount = collection.tokenData.length;
    const eligibleTokenCount = hasEligibleTokens ? totalTokenCount : 0;

    return {
      hasEligibleTokens,
      eligibleTokenCount,
      totalTokenCount,
      isLoading: eligibility.isLoading,
    };
  }, [collection, eligibility]);
};
